import { forwardRef } from "react";
import {
  Table,
  THead,
  TColumn,
  TBody,
  TRow,
  TRowExpanded,
  TCell,
  TFoot
} from "../";

import type { TableUnifiedProps } from "./TableUnified.types";
import type { TableImperativeHandle } from "../Table/Table.types";

const TableUnifiedComponent = <TableData extends Record<string, any>>(
  props: TableUnifiedProps<TableData>,
  ref: React.Ref<TableImperativeHandle>
) => {
  const {
    caption,
    columns,
    data,
    pagination = {
      isManual: true,
      state: {
        pageIndex: 1,
        pageSize: 10
      },
      totalCount: data.length,
      onChange: () => {},
      canShowPagination: true
    },
    sorting = {
      enabled: true,
      isManual: true,
      onChange: () => {}
    },
    filtering = {
      enabled: true,
      isManual: true,
      onChange: () => {}
    },
    columnResizing = { enabled: false, mode: "onChange" },
    columnOrdering = { enabled: false },
    columnPinning = { enabled: false },
    rowSelection = { enabled: false, isMultiple: false },
    rowExpansion = { enabled: false },
    rowReordering = { enabled: false },
    rowStyle,
    canEnableAllRowsSelection = true,
    isLoading,
    emptyAction,
    emptyText,
    height,
    fetchMoreOnScrollEnd,
    cellPadding
  } = props;

  return (
    <Table
      ref={ref}
      caption={caption}
      height={height}
      pagination={pagination}
      sorting={sorting}
      filtering={filtering}
      columnResizing={columnResizing}
      columnOrdering={columnOrdering}
      columnPinning={columnPinning}
      rowSelection={rowSelection}
      rowExpansion={rowExpansion}
      rowReordering={rowReordering}
      rowStyle={rowStyle}
      canEnableAllRowsSelection={canEnableAllRowsSelection}
      isLoading={isLoading}
      emptyAction={emptyAction}
      emptyText={emptyText}
      fetchMoreOnScrollEnd={fetchMoreOnScrollEnd}
      cellPadding={cellPadding}
    >
      <THead>
        {columns.map(column => (
          <TColumn
            id={column.id}
            key={column.id}
            width={column.width}
            canSort={column.canSort}
            onSort={column.onSort}
            canFilter={column.canFilter}
            filterType={column.filterType}
            filterProps={column.filterProps}
            onFilter={column.onFilter}
            canResize={column.canResize}
            cell={column.cell}
          >
            {column.header}
          </TColumn>
        ))}
      </THead>
      <TBody>
        {data.map(row => (
          <TRow key={row.id} id={row.id} row={row}>
            {columns.map(column => (
              <TCell columnId={column.id} key={column.id + row.id}>
                {row[column.id as keyof typeof row]}
              </TCell>
            ))}
            {rowExpansion.enabled && (
              <TRowExpanded label={row[columns[0]?.id as keyof typeof row]}>
                {rowExpansion?.expandRows?.[row.id]}
              </TRowExpanded>
            )}
          </TRow>
        ))}
      </TBody>
      <TFoot>
        {columns.map(column => (
          <TColumn id={column.id} key={column.id}>
            {column.footer}
          </TColumn>
        ))}
      </TFoot>
    </Table>
  );
};

export const TableUnified = forwardRef(TableUnifiedComponent) as <
  TableData extends Record<string, any>
>(
  props: TableUnifiedProps<TableData> & {
    ref?: React.Ref<TableImperativeHandle>;
  }
) => React.ReactElement;
